import { ActionIcon, Loader, Tooltip } from '@mantine/core';
import { Icon, variants } from '@uag/react-core';
import { useTranslation } from 'react-i18next';

import {
    useAutoFormBuilding,
    useAutoFormStore
} from 'app/modules/wallforming/components/auto-form/hooks/useAutoFormBuilding';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';

interface AutoFormButtonProps {
    menuOpened: boolean;
}

const screenWidth = window.screen.width;

/** One click forms every loaded wall and corner: corners, panels, connectors and anchors. Click again to cancel. */
export const AutoFormButton = ({ menuOpened }: AutoFormButtonProps) => {
    const { t } = useTranslation();
    const runAutoForm = useAutoFormBuilding();
    const isRunning = useAutoFormStore((state) => state.isRunning);
    const cancel = useAutoFormStore((state) => state.cancel);
    const hasPois = usePlacementStore((state) => state.pois.some(({ poi }) => poi.poiGuid !== EMPTY_GUID));
    const label = isRunning ? t('labelCancelAutoForm') : t('labelAutoFormBuilding');

    return (
        <Tooltip disabled={menuOpened} label={label} position="right" variant={variants.tooltip.right}>
            <ActionIcon
                aria-busy={isRunning}
                aria-label={label}
                className={`
                    group bg-background-gray-ultra-light hover:bg-background-gray! pointer-events-auto
                    ${screenWidth >= 1920 ? 'action-icon-large' : 'action-icon-medium'}
                `}
                data-testid="auto-form-button"
                disabled={isRunning ? !cancel : !hasPois}
                onClick={() => (isRunning ? cancel?.() : void runAutoForm())}
            >
                {isRunning ? (
                    <>
                        <Loader className="group-hover:hidden" size="xs" />
                        <Icon aria-hidden="true" className="hidden group-hover:inline" icon="stop" size="xl" />
                    </>
                ) : (
                    <Icon aria-hidden="true" icon="auto_awesome" size="xl" />
                )}
            </ActionIcon>
        </Tooltip>
    );
};
